import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import closeDelete from '@/assets/images/closeDelete.svg';
import infoIcon from '@/assets/images/info.svg';
import { useCurrency } from '@/context/CurrencyContext/CurrencyContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useLanguage } from '@/hooks/useLanguage';
import { useLocale } from '@/hooks/useLocale';
import { usePositionDetails } from '@/hooks/usePositionDetails';
import { Position } from '@/interfaces/Menu';
import { useBranding } from '@/utils/branding';
import { filterNonMainGroups, getImageUrl, hasSelectionRequired } from '@/utils/positionHelpers';
import ItemCardCashbackPill from '../ItemCard/ItemCardCashbackPill';
import ItemDetailsSkeleton from '../Skeletons/ItemDetailsSkeleton';
import styles from './ItemDetails.module.scss';

interface ItemDetailsProps {
  position: Position;
  menuId: string;
  onClose: () => void;
  isAvailable: boolean;
}

const ItemDetails = ({ position, menuId, onClose, isAvailable }: ItemDetailsProps) => {
  const [seeLongDescription, setSeeLongDescription] = useState(false);
  const [seeLongAllergens, setSeeLongAllergens] = useState(false);
  const shouldShowCashbackPill = useFeatureFlag('show_cashback_pill');
  const { showCashbackUI } = useBranding();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const locale = useLocale();
  const { currencySymbol } = useCurrency();

  const {
    data: detailedPosition,
    isLoading,
    error,
  } = usePositionDetails(menuId, position.id, locale);
  const displayPosition = detailedPosition || position;

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const seeMore = () => {
    setSeeLongDescription(!seeLongDescription);
  };

  const seeMoreAllergens = () => {
    setSeeLongAllergens(!seeLongAllergens);
  };

  const allergens =
    ('nutritionFacts' in displayPosition && displayPosition.nutritionFacts?.allergens) || [];
  const isDescriptionOverflow = displayPosition.description.length > 130;
  const isAllergensOverflow = allergens.join(', ').length > 130;

  if (isLoading) {
    return <ItemDetailsSkeleton onClose={onClose} />;
  }

  if (error) {
    console.error('Failed to load position details:', error);
  }

  return (
    <div
      className={styles.itemDetailsModal}
      onClick={handleBackdropClick}
      data-testid="item-details-modal"
    >
      <div className={styles.modalWrapper}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          data-testid="item-details-close-button"
        >
          <img src={closeDelete} alt={t('menu.itemDetails.close')} />
        </button>
        <div className={styles.modalContent}>
          <div className={`${styles.itemImage} ${!isAvailable && styles.unavailable}`}>
            <img
              src={getImageUrl(displayPosition)}
              alt={displayPosition.name}
              data-testid="modal-item-image"
            />
          </div>

          <div className={styles.itemInfo}>
            <h1 className={styles.itemName} data-testid="modal-item-name">
              {displayPosition.name}
            </h1>
            <p
              className={`${styles.itemDescription} ${seeLongDescription ? styles.seeLongDescription : ''} ${isDescriptionOverflow ? styles.lessMargin : ''}`}
              data-testid="modal-item-description"
            >
              {displayPosition.description}
            </p>
            {isDescriptionOverflow && (
              <p className={styles.seeMore} onClick={seeMore}>
                {seeLongDescription ? t('brandHeader.seeLess') : t('brandHeader.seeMore')}
              </p>
            )}
            {allergens.length > 0 && (
              <p
                className={`${styles.itemDescription} ${seeLongAllergens ? styles.seeLongDescription : ''} ${isAllergensOverflow ? styles.lessMargin : ''}`}
                data-testid="modal-item-allergens"
              >
                {t('menu.itemDetails.allergens')}: {allergens.join(', ')}
              </p>
            )}
            {isAllergensOverflow && (
              <p className={styles.seeMore} onClick={seeMoreAllergens}>
                {seeLongAllergens ? t('brandHeader.seeLess') : t('brandHeader.seeMore')}
              </p>
            )}
            <div className={styles.priceSection}>
              <p
                className={`${styles.price} ${!isAvailable && styles.unavailable} ${isRTL && styles.rtl}`}
                data-testid="modal-item-price"
              >
                {currencySymbol} <span>{displayPosition.price.gross.toFixed(2)}</span>
              </p>
              {showCashbackUI && shouldShowCashbackPill && displayPosition.cashbackValue && (
                <ItemCardCashbackPill
                  cashbackAmount={displayPosition.cashbackValue.cash || 0}
                  unavailable={!isAvailable}
                />
              )}
            </div>

            {hasSelectionRequired(displayPosition) &&
              'groups' in displayPosition &&
              filterNonMainGroups(displayPosition.groups).map(group => (
                <div key={group.id} className={styles.selectionSection}>
                  <div className={styles.selectionTitleContainer}>
                    <h3 className={styles.selectionTitle}>{group.name}</h3>
                    <p
                      className={`${group.restrictions.minimumSelectedElements >= 1 ? styles.requiredContainer : styles.optional}`}
                    >
                      {group.restrictions.minimumSelectedElements >= 1 ? (
                        <span className={styles.required}>
                          <img src={infoIcon} />
                          {t('menu.itemDetails.required')}
                        </span>
                      ) : (
                        t('menu.itemDetails.optional', {
                          maximumSelectedElements: group.restrictions.maximumSelectedElements,
                        })
                      )}
                    </p>
                  </div>

                  <div className={styles.selectionOptions}>
                    {group.elements.map(element => (
                      <div key={element.id} className={styles.option}>
                        {element.name}{' '}
                        {element.variant?.valueName ? `(${element.variant.valueName})` : ''}
                        {element.price && element.price.gross > 0 && (
                          <>
                            {isRTL ? (
                              <span>
                                +{element.price.gross.toFixed(2)} {currencySymbol}
                              </span>
                            ) : (
                              <span>
                                +{currencySymbol} {element.price.gross.toFixed(2)}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemDetails;
